import { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import StatCard from '../components/StatCard';
import PageHeader from '../components/PageHeader';

export default function CustomersPage() {
  const { customers, rentals, openModal, canEdit } = useApp();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const getRentalStatus = (c) => {
    const name = `${c.fname} ${c.lname}`.trim();
    const r = rentals.find(r => r.customer === name && ['active','overdue','booked'].includes(r.status));
    return r?.status || 'inactive';
  };

  const getRentalCount = (c) => {
    const name = `${c.fname} ${c.lname}`.trim();
    return rentals.filter(r => r.customer === name).length;
  };

  const getTotalSpent = (c) => {
    const name = `${c.fname} ${c.lname}`.trim();
    return rentals.filter(r => r.customer === name).reduce((s,r) => s + r.agreedPrice, 0);
  };

  const q = search.toLowerCase();
  const filtered = useMemo(() => customers.filter(c => {
    const name = `${c.fname} ${c.lname}`.trim().toLowerCase();
    const matchQ = !q || name.includes(q) || c.email?.toLowerCase().includes(q) || c.phone?.includes(q) || c.passport?.toLowerCase().includes(q);
    const status = getRentalStatus(c);
    const matchS = !filterStatus || status === filterStatus;
    return matchQ && matchS;
  }), [customers, q, filterStatus]);

  const activeCount  = customers.filter(c => getRentalStatus(c) === 'active').length;
  const overdueCount = customers.filter(c => getRentalStatus(c) === 'overdue').length;

  const STATUS_PILL = {
    active:   { cls: 's-ac', label: 'Active' },
    overdue:  { cls: 's-ov', label: 'Overdue' },
    booked:   { cls: 's-bk', label: 'Booked' },
    inactive: { cls: 's-av', label: 'Inactive' },
  };

  return (
    <div>
      <PageHeader
        title="Customers"
        icon="👥"
        subtitle="Your customer database — view rental history, contact info, documents, and damages per customer."
        actions={canEdit && canEdit('customers') && (
          <button className="btn btn-primary" onClick={() => openModal('add-customer')}>+ New Customer</button>
        )}
      />
      {/* Stats — clickable as filters */}
      <div className="sg sg-4" style={{ marginBottom: 14 }}>
        <StatCard
          label="Total Customers" value={customers.length}
          desc={(filterStatus || search) ? 'Click to clear all filters' : 'In your CRM'}
          active={!filterStatus && !search}
          onClick={() => { setFilterStatus(''); setSearch(''); }}
        />
        <StatCard
          label="Active Now" value={activeCount} variant="gr"
          desc={filterStatus === 'active' ? 'Filtering — click to clear' : 'With active rental'}
          active={filterStatus === 'active'}
          onClick={() => setFilterStatus(filterStatus === 'active' ? '' : 'active')}
        />
        <StatCard
          label="Overdue" value={overdueCount} variant="ac"
          desc={filterStatus === 'overdue' ? 'Filtering — click to clear' : 'Past return date'}
          active={filterStatus === 'overdue'}
          onClick={() => setFilterStatus(filterStatus === 'overdue' ? '' : 'overdue')}
        />
        <StatCard label="Total Revenue" value={`€${customers.reduce((s,c) => s + getTotalSpent(c), 0).toLocaleString()}`} desc="All customers" variant="bl" />
      </div>

      {/* Toolbar */}
      <div className="tb2">
        <div className="srch">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input type="text" placeholder="Search name, email, phone, passport..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="fsel" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All customers</option>
          <option value="active">Active</option>
          <option value="overdue">Overdue</option>
          <option value="booked">Booked</option>
          <option value="inactive">Inactive</option>
        </select>
        <div style={{ marginLeft: 'auto' }} />
        <button className="btn btn-primary" onClick={() => openModal('add-customer')}>+ Add Customer</button>
      </div>

      {/* Table */}
      <div className="tbl">
        <div className="tbl-hd" style={{ gridTemplateColumns: '2fr 1.5fr 1fr 1fr .8fr .6fr .5fr' }}>
          <div className="th">Name</div>
          <div className="th">Contact</div>
          <div className="th">ID / Passport</div>
          <div className="th">Rentals</div>
          <div className="th">Total Spent</div>
          <div className="th">Status</div>
          <div className="th"></div>
        </div>

        {filtered.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: 'var(--tx3)' }}>No customers match your filter</div>
        )}

        {filtered.map(c => {
          const status = getRentalStatus(c);
          const pill = STATUS_PILL[status] || STATUS_PILL.inactive;
          const count = getRentalCount(c);
          const spent = getTotalSpent(c);
          return (
            <div key={c.id} className="tr" style={{ gridTemplateColumns: '2fr 1.5fr 1fr 1fr .8fr .6fr .5fr' }}
              onClick={() => openModal('customer-profile', c.id)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: c.color || 'var(--ac)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                  {(c.fname[0] || '') + (c.lname[0] || '')}
                </div>
                <div>
                  <div className="an">{c.fname} {c.lname}</div>
                  <div className="ap">{c.nationality} · since {c.since}</div>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11 }}>{c.email}</div>
                <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{c.phone}</div>
              </div>
              <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace" }}>{c.passport || '—'}</div>
              <div style={{ fontSize: 11 }}>{count} rental{count !== 1 ? 's' : ''}</div>
              <div className="pv" style={{ fontSize: 12 }}>€{spent.toLocaleString()}</div>
              <div><span className={`sp ${pill.cls}`}>{pill.label}</span></div>
              <div className="abtns" onClick={e => e.stopPropagation()}>
                <button className="ab ab-st" onClick={() => openModal('customer-profile', c.id)}>View</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
